import type { ZoneTriggeredEventPayload, ZoneTriggeredLink } from '../types/game';
import type { ZoneKind } from './zones';

/**
 * Opis łańcucha stref – jedno źródło narracji dla historii tury (REAL) i dla logu
 * gry. Gracz ma zobaczyć każdy krok: która strefa, ile dała, dokąd przesunęła i kogo
 * po drodze wyprzedził. Sklejanie tego w jedną liczbę odbiera możliwość sprawdzenia
 * wyniku, a przy pętli czyni go wręcz niewytłumaczalnym.
 *
 * Kroki są tu STRUKTURALNE, a nie gotowym tekstem: log gry przepuszcza je przez i18n,
 * a `ScoreEntry.details` przez polski formater niżej. Jedno przejście łańcucha,
 * dwa sposoby wyświetlenia – bez powielania logiki „co się właściwie stało”.
 */

export type ZoneChainStep =
  | {
    type: 'zone';
    /** Ogniwo unieważnione przez pętlę – pokazujemy je, ale nie liczy się do wyniku. */
    voided: boolean;
    zoneKind: ZoneKind;
    start: number;
    end: number;
    delta: number;
    from: number;
    to: number;
  }
  | {
    type: 'overtake';
    voided: boolean;
    victimNames: string[];
    victimCount: number;
    bonus: number;
    to: number;
  }
  | { type: 'loop'; score: number };

const pluralGracz = (count: number) => (count === 1 ? 'gracza' : 'graczy');

const signed = (value: number) => `${value > 0 ? '+' : ''}${value}`;

/**
 * Ogniwa łańcucha z payloadu. Zdarzenia sprzed łańcuchów nie mają `links`, więc ich
 * płaskie pola są tu podnoszone do jednoogniwowej listy – reszta kodu ma wtedy jeden
 * kształt do obsłużenia.
 */
export function zoneChainLinks(payload: ZoneTriggeredEventPayload): ZoneTriggeredLink[] {
  if (Array.isArray(payload.links) && payload.links.length > 0) return payload.links;
  return [{
    zoneId: payload.zoneId,
    kind: payload.kind,
    tier: payload.tier,
    start: payload.start,
    end: payload.end,
    zoneDelta: payload.zoneDelta,
    overtakeBonus: payload.overtakeBonus,
    penalties: payload.penalties,
    triggerScore: payload.triggerScore,
    resultingScore: payload.resultingScore,
  }];
}

/** Liczba ogniw, których efekt się zaliczył. */
export function zoneChainSettledIndex(payload: ZoneTriggeredEventPayload): number {
  const links = zoneChainLinks(payload);
  const raw = Number(payload.settledIndex);
  if (!Number.isFinite(raw)) return links.length;
  return Math.max(0, Math.min(links.length, raw));
}

/**
 * Rozkłada łańcuch na kroki do pokazania. `nameOf` tłumaczy id ofiary na nazwę;
 * gdy gracza nie ma już w stanie, wystarczy zwrócić pusty string – nazwa zostanie pominięta.
 */
export function zoneChainSteps(
  payload: ZoneTriggeredEventPayload,
  nameOf: (playerId: string) => string,
): ZoneChainStep[] {
  const links = zoneChainLinks(payload);
  const settledIndex = zoneChainSettledIndex(payload);
  const steps: ZoneChainStep[] = [];

  links.forEach((link, index) => {
    const voided = index >= settledIndex;
    steps.push({
      type: 'zone',
      voided,
      zoneKind: link.kind,
      start: link.start,
      end: link.end,
      delta: link.zoneDelta,
      from: link.triggerScore,
      to: link.triggerScore + link.zoneDelta,
    });
    if (link.penalties.length > 0) {
      steps.push({
        type: 'overtake',
        voided,
        victimNames: link.penalties.map((penalty) => nameOf(penalty.playerId)).filter(Boolean),
        victimCount: link.penalties.length,
        bonus: link.overtakeBonus,
        to: link.resultingScore,
      });
    }
  });

  if (payload.loop) steps.push({ type: 'loop', score: payload.resultingScore });

  return steps;
}

/**
 * Polski opis krok po kroku – dla `ScoreEntry.details`, gdzie reszta rozliczenia tury
 * też jest po polsku (kary, wyprzedzenia z settlementu).
 */
export function describeZoneChain(
  payload: ZoneTriggeredEventPayload,
  nameOf: (playerId: string) => string,
): string[] {
  return zoneChainSteps(payload, nameOf).map((step) => {
    if (step.type === 'loop') {
      return `Pętla stref – gracz zatrzymany na najgorszym kroku: ${step.score}`;
    }
    const prefix = step.voided ? '[pętla] ' : '';
    if (step.type === 'zone') {
      const label = step.zoneKind === 'bonus' ? 'premia' : 'kara';
      return `${prefix}Strefa ${step.start}-${step.end} (${label} ${signed(step.delta)}): ${step.from} → ${step.to}`;
    }
    const who = step.victimNames.length > 0 ? ` (${step.victimNames.join(', ')})` : '';
    return `${prefix}Wyprzedzono ${step.victimCount} ${pluralGracz(step.victimCount)}${who}: ${signed(step.bonus)} → ${step.to}`;
  });
}
